<script setup>
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Checkbox from 'primevue/checkbox'
import Dropdown from 'primevue/dropdown'
import { uploadImage, getGalleryImages } from '@/shared/api/files.service'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  category: {
    type: Object,
    default: null
  },
  categories: {
    type: Array,
    default: () => []
  },
  mode: {
    type: String,
    default: 'create'
  },
  submitting: {
    type: Boolean,
    default: false
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'submit'])

const name = ref('')
const isActive = ref(true)
const parentId = ref(null)
const localErrors = ref({})

const imageUrl = ref('')
const uploading = ref(false)
const galleryVisible = ref(false)
const galleryImages = ref([])
const fileInput = ref(null)

const urlDialogVisible = ref(false)
const urlInput = ref('')

const dialogTitle = computed(() =>
  props.mode === 'edit' ? 'Редактирование категории' : 'Создание категории'
)

const submitLabel = computed(() =>
  props.mode === 'edit' ? 'Сохранить' : 'Создать'
)

const submitIcon = computed(() =>
  props.mode === 'edit' ? 'pi pi-check' : 'pi pi-plus'
)

const parentOptions = computed(() => {
  const currentCategoryId = props.category?.id ?? null

  const baseOptions = [
    { label: 'Без родительской категории', value: null }
  ]

  const categoryOptions = props.categories
    .filter(category => category.id !== currentCategoryId)
    .map(category => ({
      label: category.name,
      value: category.id
    }))

  return [...baseOptions, ...categoryOptions]
})

watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      name.value = props.category?.name ?? ''
      isActive.value = props.category?.isActive ?? true
      parentId.value = props.category?.parentId ?? null
      imageUrl.value = props.category?.imageUrl ?? ''
      localErrors.value = {}
    } else {
      resetForm()
    }
  }
)

watch(
  () => props.category,
  (newCategory) => {
    if (props.visible) {
      name.value = newCategory?.name ?? ''
      isActive.value = newCategory?.isActive ?? true
      parentId.value = newCategory?.parentId ?? null
      imageUrl.value = newCategory?.imageUrl ?? ''
    }
  }
)

function resetForm() {
  name.value = ''
  isActive.value = true
  parentId.value = null
  imageUrl.value = ''
  localErrors.value = {}
}

function getFieldError(field) {
  return (
    localErrors.value[field] ||
    props.fieldErrors?.[field] ||
    props.fieldErrors?.[field.charAt(0).toUpperCase() + field.slice(1)] ||
    ''
  )
}

function hasFieldError(field) {
  return Boolean(getFieldError(field))
}

function validateForm() {
  localErrors.value = {}

  if (!name.value.trim()) {
    localErrors.value.name = 'Введите название категории'
  }

  return Object.keys(localErrors.value).length === 0
}

function triggerFileUpload() {
  fileInput.value?.click()
}

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml']

async function onFileSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return

  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    alert(`Формат файла не поддерживается: ${file.type || 'неизвестный'}\nДопустимые форматы: JPG, PNG, GIF, WebP, BMP, SVG`)
    if (fileInput.value) fileInput.value.value = ''
    return
  }

  uploading.value = true

  try {
    const response = await uploadImage(file, 'categories')
    imageUrl.value = response.url
  } catch (error) {
    console.error('Ошибка загрузки изображения', error)
    alert('Не удалось загрузить изображение: ' + (error.response?.data?.message || error.message))
  } finally {
    uploading.value = false
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

function addImageByUrl() {
  urlInput.value = ''
  urlDialogVisible.value = true
}

function confirmImageUrl() {
  if (urlInput.value && urlInput.value.trim()) {
    imageUrl.value = urlInput.value.trim()
  }
  urlDialogVisible.value = false
}

async function openGallery() {
  await loadGalleryImages('categories')
  galleryVisible.value = true
}

async function loadGalleryImages(type = 'products') {
  try {
    const images = await getGalleryImages(type)
    galleryImages.value = images
  } catch (error) {
    console.error('Ошибка загрузки галереи:', error)
    galleryImages.value = []
  }
}

function selectFromGallery(url) {
  imageUrl.value = url
  galleryVisible.value = false
}

function removeImage() {
  imageUrl.value = ''
}

function submitForm() {
  if (!validateForm()) return

  const payload = {
    name: name.value.trim(),
    isActive: isActive.value,
    parentId: parentId.value,
    imageUrl: imageUrl.value || null
  }

  emit('submit', payload)
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :header="dialogTitle"
    :style="{ width: 'min(600px, 95vw)' }"
    @update:visible="emit('close')"
  >
    <div class="form-container">
      <div class="field">
        <label for="category-name">Название категории</label>
        <InputText
          id="category-name"
          v-model="name"
          placeholder="Введите название категории"
          class="w-full"
          :invalid="hasFieldError('name')"
          @input="localErrors.name = ''"
        />
        <small v-if="hasFieldError('name')" class="field-error">
          {{ getFieldError('name') }}
        </small>
      </div>

      <div class="field">
        <label for="category-parent">Родительская категория</label>
        <Dropdown
          id="category-parent"
          v-model="parentId"
          :options="parentOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Выберите родительскую категорию"
          class="w-full"
          :invalid="hasFieldError('parentId')"
        />
        <small v-if="hasFieldError('parentId')" class="field-error">
          {{ getFieldError('parentId') }}
        </small>
      </div>

      <div class="checkbox-field">
        <Checkbox
          v-model="isActive"
          inputId="category-active"
          binary
        />
        <label for="category-active">Активная категория</label>
      </div>

      <div class="images-block">
        <div class="images-header">
          <label>Изображение категории</label>
          <div class="images-actions">
            <Button
              label="По URL"
              icon="pi pi-link"
              severity="secondary"
              outlined
              size="small"
              @click="addImageByUrl"
            />
            <Button
              label="Загрузить файл"
              icon="pi pi-upload"
              severity="secondary"
              outlined
              size="small"
              @click="triggerFileUpload"
              :loading="uploading"
            />
            <Button
              label="Из галереи"
              icon="pi pi-images"
              severity="secondary"
              outlined
              size="small"
              @click="openGallery"
            />
          </div>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,image/svg+xml"
          style="display: none"
          @change="onFileSelected"
        />

        <div v-if="imageUrl" class="image-preview">
          <img :src="imageUrl" alt="Category image" />
          <Button
            icon="pi pi-times"
            severity="danger"
            outlined
            size="small"
            @click="removeImage"
          />
        </div>

        <div v-else-if="!uploading" class="image-placeholder">
          <i class="pi pi-image"></i>
          <span>Нет изображения</span>
        </div>

        <div v-if="uploading" class="uploading-status">
          <i class="pi pi-spin pi-spinner"></i>
          Загрузка изображения...
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="urlDialogVisible"
      header="Добавить изображение по URL"
      :style="{ width: 'min(450px, 95vw)' }"
      modal
    >
      <div class="field" style="margin-top: 8px;">
        <label for="image-url-input">URL изображения</label>
        <InputText
          id="image-url-input"
          v-model="urlInput"
          placeholder="https://example.com/image.jpg"
          class="w-full"
          @keyup.enter="confirmImageUrl"
        />
      </div>
      <template #footer>
        <Button
          label="Отмена"
          severity="secondary"
          outlined
          @click="urlDialogVisible = false"
        />
        <Button
          label="Применить"
          icon="pi pi-check"
          @click="confirmImageUrl"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="galleryVisible"
      header="Галерея изображений"
      :style="{ width: 'min(800px, 95vw)' }"
      modal
    >
      <div class="gallery-grid">
        <div
          v-for="img in galleryImages"
          :key="img.url"
          class="gallery-item"
          @click="selectFromGallery(img.url)"
        >
          <img :src="img.url" :alt="img.name" />
          <div class="gallery-item-name">{{ img.name }}</div>
        </div>

        <div v-if="galleryImages.length === 0" class="gallery-empty">
          Нет загруженных изображений
        </div>
      </div>

      <template #footer>
        <Button
          label="Закрыть"
          severity="secondary"
          @click="galleryVisible = false"
        />
      </template>
    </Dialog>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        :disabled="props.submitting"
        @click="emit('close')"
      />
      <Button
        :label="submitLabel"
        :icon="submitIcon"
        :loading="props.submitting"
        :disabled="props.submitting || uploading"
        @click="submitForm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.field-error {
  font-size: 12px;
  color: #dc2626;
}

:deep(.p-inputtext),
:deep(.p-dropdown) {
  border-radius: 12px;
}

:deep(.p-inputtext.p-invalid),
:deep(.p-dropdown.p-invalid) {
  border-color: #dc2626;
}

.images-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.images-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.images-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.image-preview {
  position: relative;
  width: 100%;
  max-width: 200px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.image-preview .p-button {
  position: absolute;
  top: 8px;
  right: 8px;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 200px;
  height: 150px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
  color: #999;
}

.image-placeholder i {
  font-size: 32px;
}

.uploading-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #e3f2fd;
  border-radius: 6px;
  font-size: 13px;
  color: #1976d2;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
}

.gallery-item {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  text-align: center;
}

.gallery-item:hover {
  border-color: #c41217;
  transform: scale(1.02);
}

.gallery-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.gallery-item-name {
  padding: 8px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

.gallery-empty {
  text-align: center;
  padding: 40px;
  color: #999;
}
</style>